import { useCallback, useEffect, useRef, useState } from 'react';
import type { AutosaveStatus } from '../../../../hooks/useAutosave';

/** Save status for one-shot saves that go out straight away (mode switch, toggles,
 *  per-item stage, ladder list actions), in the same shape as an autosave
 *  so the page can merge it into its one status line. `track` runs a save
 *  (a thunk, so `retry` can send the failed ones again). The edit stays as made on
 *  failure, like an autosave draft. */
export function useTracker() {
  const [pending, setPending] = useState(0);
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const failed = useRef<(() => Promise<unknown>)[]>([]);

  const run = useCallback(<R,>(fn: () => Promise<R>): Promise<R> => {
    setPending((n) => n + 1);
    setStatus('saving');
    return fn().then(
      (r) => {
        setPending((n) => n - 1);
        return r;
      },
      (e: unknown) => {
        failed.current.push(fn);
        setPending((n) => n - 1);
        setError(e instanceof Error ? e.message : String(e));
        throw e;
      },
    );
  }, []);

  useEffect(() => {
    if (pending > 0) return;
    setStatus((s) => (s !== 'saving' ? s : failed.current.length ? 'error' : 'saved'));
  }, [pending]);

  useEffect(() => {
    if (status !== 'saved') return;
    const h = window.setTimeout(() => setStatus('idle'), 2500);
    return () => window.clearTimeout(h);
  }, [status]);

  const retry = useCallback(() => {
    const again = failed.current;
    failed.current = [];
    setError(null);
    again.forEach((fn) => void run(fn).catch(() => {}));
  }, [run]);

  return { status, error, retry, track: run };
}
